import { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { unitConverterStore } from '../stores/unit-converter-store';
import './unit-converter-ui.scss';

export const UnitConverterUI = observer(() => {
  const [showHistory, setShowHistory] = useState(false);

  const formatDate = (timestamp: number) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(timestamp));
  };

  const formatHistoryValue = (value: number) => {
    if (Math.abs(value) >= 1000000 || (Math.abs(value) < 0.001 && value !== 0)) {
      return value.toExponential(3);
    }
    return value.toFixed(6).replace(/\.?0+$/, '');
  };

  return (
    <div className="unit-converter">
      <div className="unit-converter__main">
        <div className="unit-converter__header">
          <h2>Unit Converter</h2>
          <div className="unit-converter__header-actions">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className={`unit-converter__btn unit-converter__btn--secondary ${showHistory ? 'unit-converter__btn--active' : ''}`}
            >
              📋 History
            </button>
          </div>
        </div>

        <div className="unit-converter__category-selector">
          <div className="unit-converter__category-grid">
            {unitConverterStore.categories.map((category) => (
              <button
                key={category.id}
                onClick={() => unitConverterStore.selectCategory(category.id)}
                className={`unit-converter__category-btn ${
                  unitConverterStore.selectedCategoryId === category.id
                    ? 'unit-converter__category-btn--active'
                    : ''
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>

        <div className="unit-converter__converter">
          <div className="unit-converter__input-section">
            <label className="unit-converter__label">From</label>
            <div className="unit-converter__input-group">
              <input
                type="number"
                value={unitConverterStore.inputValue}
                onChange={(e) => unitConverterStore.setInputValue(e.target.value)}
                placeholder="Enter value..."
                className="unit-converter__input"
              />
              <select
                value={unitConverterStore.fromUnit?.id || ''}
                onChange={(e) => unitConverterStore.setFromUnit(e.target.value)}
                className="unit-converter__select"
              >
                {unitConverterStore.selectedCategory?.units.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.name} ({unit.symbol})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="unit-converter__swap-section">
            <button
              onClick={() => unitConverterStore.swapUnits()}
              className="unit-converter__swap-btn"
              title="Swap units"
            >
              ⇅
            </button>
          </div>

          <div className="unit-converter__output-section">
            <label className="unit-converter__label">To</label>
            <div className="unit-converter__input-group">
              <div className="unit-converter__result">
                {unitConverterStore.formattedResult}
              </div>
              <select
                value={unitConverterStore.toUnit?.id || ''}
                onChange={(e) => unitConverterStore.setToUnit(e.target.value)}
                className="unit-converter__select"
              >
                {unitConverterStore.selectedCategory?.units.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.name} ({unit.symbol})
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="unit-converter__conversion-summary">
          {unitConverterStore.fromUnit && unitConverterStore.toUnit && (
            <div className="unit-converter__summary-text">
              {unitConverterStore.inputValue} {unitConverterStore.fromUnit.symbol} = {' '}
              <strong>{unitConverterStore.formattedResult} {unitConverterStore.toUnit.symbol}</strong>
            </div>
          )}
        </div>

        <div className="unit-converter__quick-conversions">
          {unitConverterStore.selectedCategory && (
            <>
              <h3>Quick Reference</h3>
              <div className="unit-converter__quick-grid">
                {unitConverterStore.selectedCategory.units.slice(0, 6).map((unit) => {
                  if (!unitConverterStore.fromUnit || unit.id === unitConverterStore.fromUnit.id) return null;
                  
                  let quickResult: number;
                  const inputValue = parseFloat(unitConverterStore.inputValue) || 1;
                  
                  if (unitConverterStore.selectedCategory?.id === 'temperature') {
                    // Use store's temperature conversion method
                    const baseValue = inputValue * unitConverterStore.fromUnit.conversionFactor;
                    quickResult = baseValue / unit.conversionFactor;
                  } else {
                    const baseValue = inputValue * unitConverterStore.fromUnit.conversionFactor;
                    quickResult = baseValue / unit.conversionFactor;
                  }
                  
                  return (
                    <div
                      key={unit.id}
                      onClick={() => unitConverterStore.setToUnit(unit.id)}
                      className="unit-converter__quick-item"
                    >
                      <div className="unit-converter__quick-value">
                        {formatHistoryValue(quickResult)}
                      </div>
                      <div className="unit-converter__quick-unit">{unit.symbol}</div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {showHistory && (
        <div className="unit-converter__history">
          <div className="unit-converter__history-header">
            <h3>Conversion History</h3>
            <div className="unit-converter__history-actions">
              <button
                onClick={() => unitConverterStore.clearHistory()}
                className="unit-converter__btn unit-converter__btn--danger unit-converter__btn--small"
                disabled={unitConverterStore.history.length === 0}
              >
                Clear All
              </button>
            </div>
          </div>

          <div className="unit-converter__history-list">
            {unitConverterStore.history.length === 0 ? (
              <div className="unit-converter__history-empty">
                No conversion history yet
              </div>
            ) : (
              unitConverterStore.history.map((item) => (
                <div
                  key={item.id}
                  onClick={() => unitConverterStore.useHistoryConversion(item)}
                  className="unit-converter__history-item"
                >
                  <div className="unit-converter__history-conversion">
                    <div className="unit-converter__history-from">
                      {formatHistoryValue(item.fromValue)} {item.fromUnit.symbol}
                    </div>
                    <div className="unit-converter__history-arrow">→</div>
                    <div className="unit-converter__history-to">
                      {formatHistoryValue(item.toValue)} {item.toUnit.symbol}
                    </div>
                  </div>
                  <div className="unit-converter__history-meta">
                    <span className="unit-converter__history-category">{item.category}</span>
                    <span className="unit-converter__history-date">
                      {formatDate(item.timestamp)}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        unitConverterStore.deleteHistoryItem(item.id);
                      }}
                      className="unit-converter__history-delete"
                      title="Delete this conversion"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
});
